import {
  CalendarDate,
  isToday as _isToday,
  createCalendar,
  getLocalTimeZone,
  getWeeksInMonth,
  today
} from '@internationalized/date'
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react'
import React, { useMemo } from 'react'
import {
  CalendarProps,
  DateValue,
  useButton,
  useCalendar,
  useCalendarCell,
  useCalendarGrid,
  useLocale
} from 'react-aria'
import { CalendarState, RangeCalendarState, useCalendarState } from 'react-stately'
import { cn } from '@/lib/utils'
import { Heading } from '..'
import { Button, ButtonProps } from '../button'
import { cva } from 'class-variance-authority'

const calendarVariants = cva('', {
  variants: {
    calendarButtonVariant: {
      default: 'bg-white text-on-surface',
      inverted: '!bg-surface-inverted text-on-surface-inverted'
    }
  },
  defaultVariants: {
    calendarButtonVariant: 'default'
  }
})

interface CalendarDateProps extends CalendarProps<DateValue> {
  variant?: 'default' | 'inverted'
}

function Calendar(props: CalendarDateProps) {
  const prevButtonRef = React.useRef<HTMLButtonElement | null>(null)
  const nextButtonRef = React.useRef<HTMLButtonElement | null>(null)

  const { locale } = useLocale()
  const state = useCalendarState({
    ...props,
    locale,
    createCalendar
  } as any)
  const {
    calendarProps,
    prevButtonProps: _prevButtonProps,
    nextButtonProps: _nextButtonProps,
    title
  } = useCalendar(props, state as any)
  const {
    buttonProps: { className: prevButtonClassName, ...prevButtonProps }
  } = useButton(_prevButtonProps, prevButtonRef)
  const {
    buttonProps: { className: nextButtonClassName, ...nextButtonProps }
  } = useButton(_nextButtonProps, nextButtonRef)

  const isInverted = props.variant === 'inverted'

  return (
    <div {...calendarProps} className="space-y-4">
      <div className="relative flex items-center justify-center pt-1">
        <CalendarButton
          {...prevButtonProps}
          ref={prevButtonRef}
          className={cn(
            prevButtonClassName,
            'absolute left-1 h-7 w-7 p-0',
            calendarVariants({ calendarButtonVariant: props.variant })
          )}
        >
          <ChevronLeftIcon className={cn('h-4 w-4', isInverted ? 'stroke-on-surface-inverted' : '')} />
        </CalendarButton>
        <Heading weight="medium">{title}</Heading>
        <CalendarButton
          {...nextButtonProps}
          ref={nextButtonRef}
          className={cn('absolute right-1 h-7 w-7 p-0', calendarVariants({ calendarButtonVariant: props.variant }))}
        >
          <ChevronRightIcon className={cn('h-4 w-4', isInverted ? 'stroke-on-surface-inverted' : '')} />
        </CalendarButton>
      </div>
      <CalendarGrid state={state} variant={props.variant} />
    </div>
  )
}

interface CalendarGridProps {
  state: CalendarState | RangeCalendarState
  variant?: 'default' | 'inverted'
}

function CalendarGrid({ state, variant, ...props }: CalendarGridProps) {
  const { locale } = useLocale()
  const { gridProps, headerProps, weekDays } = useCalendarGrid(props, state as any)

  // Get the number of weeks in the month so we can render the proper number of rows.
  const weeksInMonth = getWeeksInMonth(state.visibleRange.start, locale)
  const isInverted = variant === 'inverted'

  return (
    <table
      {...gridProps}
      className={cn(
        gridProps.className,
        'w-full border-collapse space-y-1',
        variant === 'inverted' ? 'bg-surface-inverted text-on-surface-inverted' : 'text-on-surface bg-white'
      )}
    >
      <thead {...headerProps}>
        <tr className="flex">
          {weekDays.map((day, index) => (
            <th
              className={cn(
                'w-9 rounded-md text-[0.8rem] font-normal',
                isInverted ? 'text-on-surface-inverted' : 'text-muted-foreground'
              )}
              key={index}
            >
              {day}
            </th>
          ))}
        </tr>
      </thead>
      <tbody className="space-y-2">
        {[...new Array(weeksInMonth).keys()].map((weekIndex) => (
          <tr className={cn('flex w-full', isInverted ? 'text-on-surface-inverted' : '')} key={weekIndex}>
            {state.getDatesInWeek(weekIndex).map((date, i) => {
              return date ? <CalendarCell key={i} state={state as any} date={date} variant={variant} /> : <td key={i} />
            })}
          </tr>
        ))}
      </tbody>
    </table>
  )
}

interface CalendarCellProps {
  state: CalendarState
  date: CalendarDate
  variant?: 'default' | 'inverted'
}

function CalendarCell({ state, date, variant }: CalendarCellProps) {
  const ref = React.useRef<HTMLButtonElement | null>(null)
  const { cellProps, buttonProps, isSelected, isOutsideVisibleRange, isDisabled, formattedDate } = useCalendarCell(
    { date } as any,
    state as any,
    ref
  )

  const isInverted = variant === 'inverted'
  const isToday = useMemo(() => {
    const timezone = getLocalTimeZone()
    return _isToday(date, timezone)
  }, [date])

  const dateIsBeforeToday = date.compare(today(getLocalTimeZone())) < 0
  return (
    <td
      {...cellProps}
      className={cn(
        cellProps.className,
        '[&:has([aria-selected])]:bg-accent relative p-0 text-center text-sm focus-within:relative focus-within:z-20 first:[&:has([aria-selected])]:rounded-l-md last:[&:has([aria-selected])]:rounded-r-md',
        isInverted ? 'text-on-surface-inverted' : 'text-on-surface'
      )}
    >
      <CalendarButton
        {...buttonProps}
        type="button"
        ref={ref}
        disabled={dateIsBeforeToday}
        className={cn(
          buttonProps.className,
          'hover:bg-surface-container-inverted hover:text-on-surface-inverted h-9 w-9',
          isToday ? 'bg-accent text-accent-foreground' : '',
          isSelected
            ? isInverted
              ? 'bg-primary-inverted text-on-primary-inverted hover:bg-surface-container-inverted-hover hover:text-on-surface-inverted active:text-on-surface-inverted focus-visible:ring-secondary'
              : 'bg-secondary text-on-secondary hover:bg-secondary-hover hover:text-on-secondary active:text-on-secondary focus:hover:bg-secondary-hover focus:text-on-secondary focus-visible:ring-secondary'
            : '',
          isOutsideVisibleRange ? 'text-muted-foreground opacity-50' : '',
          isDisabled ? 'opacity-60' : ''
        )}
      >
        {formattedDate}
      </CalendarButton>
    </td>
  )
}

const CalendarButton = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ children, className, defaultChecked, ...props }, ref) => {
    return (
      <Button
        ref={ref}
        className={cn(
          'leading-xs focus-visible:ring-primary ring-offset-background hover:text-on-secondary hover:bg-secondary-hover inline-flex h-10 items-center justify-center space-x-1 rounded px-4 py-2 text-sm text-inherit transition-colors focus-visible:ring-2 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50',
          className
        )}
        {...props}
      >
        {children}
      </Button>
    )
  }
)
CalendarButton.displayName = 'CalendarButton'

export { Calendar }
